// required
import '@sentry/tracing'

import { CATEGORIES } from '@kijiji/category'
import isMobile from 'ismobilejs'
import { type GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { getServerSession } from 'next-auth/next'
import { useSession } from 'next-auth/react'
import qs from 'query-string'
import { useEffect, useState } from 'react'
import { useGetSearchResultsData } from 'src/hooks/srp/useGetSearchResultsData'
import { useSearchLoadingState } from 'src/hooks/srp/useSearchLoadingState'
import { useExtendedSearch } from 'src/hooks/useExtendedSearch'
import { useFavouriteListingUpdate } from 'src/hooks/useFavouriteListingUpdate'
import { ROUTE_LOCALE, useLocale } from 'src/hooks/useLocale'
import { useSaveSearchFromLogin } from 'src/hooks/useSaveSearchFromLogin'
import { useTheme } from 'styled-components'

import { AutoChangeRadiusLocationSystemMessage } from '@/components/shared/auto-change-radius-location-system-message'
import { ContentfulContent } from '@/components/shared/contentful-content/ContentfulContent'
import { ErrorBoundary } from '@/components/shared/error-boundary'
import { BaseLayout } from '@/components/shared/layouts/BaseLayout'
import { PageContainer } from '@/components/shared/page-container'
import { SrpGrid, SrpHeader, SrpResultsSort } from '@/components/srp'
import { Breadcrumbs } from '@/components/srp/breadcrumbs'
import { FiltersSidebar } from '@/components/srp/filters/FiltersSidebar'
import { TicketListingCard } from '@/components/srp/listing-card/ticket-listing-card/TicketListingCard'
import { PopularRelatedKeywords } from '@/components/srp/popular-related-keywords/PopularRelatedKeywords'
import { SearchFloatingButtons } from '@/components/srp/search-floating-buttons'
import { SearchList } from '@/components/srp/search-list'
import { SearchAdsWrapper } from '@/components/srp/SearchAdsWrapperWithProps'
import { SeoLinks } from '@/components/srp/seo-links'
import { SeoMetadata } from '@/components/srp/seo-metadata'
import { SrpError } from '@/components/srp/srp-error'
import { CookieRegistry } from '@/constants/cookieRegistry'
import { PAGE_TYPE } from '@/constants/pageTypes'
import { type SortByKeys } from '@/constants/search'
import { SearchContext, SearchProvider } from '@/contexts/SearchContext'
import { SrpPageProvider } from '@/contexts/SrpContext'
import { COOKIE_LOCALE, getLocale } from '@/domain/locale'
import { getLocationArea } from '@/domain/location/getLocationArea'
import {
  getFallbackErrorSearchPageProps,
  getSearchPagePropsFromQuery,
} from '@/domain/srp/getSearchPagePropsFromQuery'
import { getSrpComposedUrl } from '@/domain/srp/getSrpComposedUrl'
import {
  type GetSrpPropsWithCache,
  type PageRedirect,
  getSrpLoadData,
} from '@/domain/srp/getSrpLoadData'
import { redirectLocationlessUrl } from '@/domain/srp/page-redirects/redirectLocationlessUrl'
import { decodeUrl } from '@/domain/srp/urlDecode'
import {
  type AttributeFilter,
  type AttributeFilterInput,
  type DateFilterInput,
  type DateRangeFilterInput,
  type RangeFilterInput,
  type SearchCategory,
  type SearchCategoryPath,
  type SearchUrlPopularRelatedInput,
  type SortOption,
} from '@/generated'
import { BaseLeaderboardAdSlot, TopLeaderboardAdSlot } from '@/lib/ads/components/ad-slots'
import { AdSense } from '@/lib/ads/components/adsense'
import ClarivoyScript from '@/lib/ads/components/clarivoy/clarivoyScript'
import useClarivoy from '@/lib/ads/components/clarivoy/useClarivoy'
import { type GlobalLocation } from '@/lib/apollo/locationCache'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { useSrpTracking } from '@/lib/ga/hooks/useSrpTracking'
import { type ContentfulDataContent } from '@/types/contentful'
import { type AppliedFilters, type Listing, type SearchFiltering } from '@/types/search'
import { Flex } from '@/ui/atoms/flex'
import { extractIpv4 } from '@/utils/extractIpv4'
import { logger } from '@/utils/sendToLogger'
import { serverSideTranslations } from '@/utils/serverSideTranslations'

import { getServerAuthOptions } from '../../api/auth/[...nextauth]'

const AppPenRealEstateModal = dynamic(
  () => import('@/components/srp/AppPenRealEstateModal').then((mod) => mod.AppPenRealEstateModal),
  { ssr: false }
)

export type SRPErrors = {
  hasServerError?: boolean
  hasResultsError?: boolean
}

export type SearchPageServerProps = {
  /**
   * Error states from SRP Anvil calls
   */
  errors?: SRPErrors
  /**
   * An ID that is used to identify the page load. This allows us to detect new
   * page loads where the url hasn't changed.
   */
  pageLoadId: string
  /**
   * The device used to browse Kijiji
   */
  userAgent?: string
}

export type SearchPageProps = SearchPageServerProps & {
  /**
   * The number of the current page
   */
  currentPage: number
  /**
   * All the filters used for this specific category
   */
  filters: SearchFiltering
  /**
   * Error states from SRP Anvil calls
   */
  errors?: SRPErrors
  /**
   * Filters applied to the initial search
   */
  appliedFilters: AppliedFilters
  /**
   * Raw search query string used to save a search
   * This formatted value is sent with the page results data
   */
  searchString: string
  /**
   * Category name used in the title and breadcrumb for example
   */
  category: SearchCategory
  /**
   * Parent path of category
   */
  categoryPath: SearchCategoryPath[]
  /**
   * Location used in the search
   */
  location: GlobalLocation
  /*
   * The keywords separated by a space
   */
  keywords: string
  /**
   * The max limit of ads per page
   */
  limit: number
  /**
   * Search listings
   */
  listings?: Listing[]
  /**
   * sortKey from query parameter
   */
  sortKey: SortByKeys
  /**
   * The sorting options available for the selected category
   */
  sorting: SortOption[]
  /**
   * Contentful data
   */
  textJson: ContentfulDataContent | null
  /**
   * Top Ads listings
   */
  topAds: Listing[]
  /**
   * Total number of listings in all pages
   */
  totalListings: number

  locationTree: AttributeFilter | null
  categoryTree: AttributeFilter | null
  attributeFilters: AttributeFilterInput[]
  rangeFilters: RangeFilterInput[]
  dateRangeFilters: DateRangeFilterInput[]
  dateFilters: DateFilterInput[]
}

const SearchPage = ({ pageLoadId, userAgent }: SearchPageServerProps) => {
  const { setLoadingStates } = useSearchLoadingState()
  const [hasClientFetchAttemptError, setHasClientFetchAttemptError] = useState(false)
  const { asPath, query, replace } = useRouter()
  const { routeLocale } = useLocale()

  const { data: userData } = useSession()
  const { updateFavourite } = useFavouriteListingUpdate()

  const srpUrl = getSrpComposedUrl(query)
  const decodedUrl = decodeUrl(srpUrl)

  const isMobileForAds = isMobile(userAgent).phone

  const {
    data: srpData,
    loading: srpLoading,
    called: srpCalled,
  } = useGetSearchResultsData({
    fetchPolicy: 'cache-first',
    onError: () => {
      setHasClientFetchAttemptError(true)
      setLoadingStates(false)
    },
    skip: hasClientFetchAttemptError,
  })

  const isInitialLoad = !srpData && srpLoading

  const props = srpData
    ? getSearchPagePropsFromQuery(srpUrl, decodedUrl, routeLocale, srpData)
    : getFallbackErrorSearchPageProps() // TODO: We need to remove this

  const {
    category: initialCategory,
    currentPage,
    keywords,
    limit,
    listings = [],
    location: initialLocation,
    sortKey,
    textJson,
    totalListings,
    errors: fallbackErrors,
  } = props

  const errors = {
    hasServerError: fallbackErrors?.hasServerError,
    hasResultsError: srpCalled && !srpLoading && srpData?.results === null,
  }

  /**
   * UseEffect to be triggered if the user has signed in to favourite a listing
   * The decision is made based on a query parameter
   * Once the function has been triggered the query parameter should be removed from the URL
   */
  useEffect(() => {
    const listingId = query.listingId
    if (typeof listingId !== 'string' || !userData?.user) return
    /**
     * Remove listingId from the query params
     * If the initial value of listingId is not overwritten, the request will happen multiple times
     */
    delete query.listingId
    const { url } = qs.parseUrl(asPath)
    replace(qs.stringifyUrl({ url, query }))

    updateFavourite(false, {
      variables: { listingId },
      onCompleted: () => {
        trackEvent({
          action: GA_EVENT.WatchlistAdd,
          label: `adId=${listingId}`,
        })
      },
      onError: (error) => {
        // catch this error legitimate error
        if (error.message.includes('already favourited')) {
          return
        }
      },
    })
  }, [userData, asPath, updateFavourite, query, replace])

  useSaveSearchFromLogin({ searchString: srpData?.searchQuery.searchString })
  useSrpTracking()

  const { spacing } = useTheme()

  const { extendedListings } = useExtendedSearch({
    categoryId: initialCategory.id,
    keywords,
    limit,
    location: initialLocation,
    organicListings: errors.hasResultsError || errors.hasServerError ? [] : listings,
    page: currentPage,
    sortKey: sortKey,
    totalListingCount: totalListings,
  })

  const { isScriptLoadable: isClarivoyScriptLoadable } = useClarivoy({
    listings,
    category: initialCategory,
    pageType: PAGE_TYPE.SEARCH_RESULTS_PAGE,
  })

  const popularRelatedUrlInput: SearchUrlPopularRelatedInput = {
    categoryId: props.category.id.toString(),
    location: { id: props.location.id },
    keywords: props.keywords || undefined,
  }

  const renderSearchListings = (seeAllTopAds: () => Promise<void>) => {
    if (errors.hasResultsError) {
      return <SrpError />
    }

    if (initialCategory.id === CATEGORIES.TICKETS_CATEGORY_ID) {
      return <TicketListingCard />
    }

    return (
      <SearchList
        extendedRadiusListings={extendedListings}
        handleSeeAllTopAds={seeAllTopAds}
        totalPageCount={Math.ceil(totalListings / limit)}
        isMobile={isMobileForAds}
      />
    )
  }

  return (
    <>
      {isClarivoyScriptLoadable && <ClarivoyScript defer />}

      <SeoMetadata pageLoadId={pageLoadId}>
        {(pageH1) => (
          <SrpPageProvider searchPageProps={{ ...props, pageLoadId, userAgent, errors }}>
            <SearchProvider>
              <SearchContext.Consumer>
                {({ handleNewSearch, hasRefetchUrlError, seeAllTopAds }) => (
                  <SearchAdsWrapper isMobileForAds={isMobileForAds}>
                    <>
                      <BaseLayout
                        handleSearchSubmit={handleNewSearch}
                        showSearchBar={true}
                        withAuthModal={true}
                        includeRUMScripts={true}
                        rumPageLabel="SRP"
                      >
                        <ErrorBoundary>
                          {/* If there is server-side search or client-side refetch url error */}
                          {hasClientFetchAttemptError || hasRefetchUrlError ? (
                            <SrpError />
                          ) : (
                            <>
                              <PageContainer bottom={spacing.default} top={spacing.default}>
                                <PopularRelatedKeywords
                                  {...popularRelatedUrlInput}
                                  locationArea={getLocationArea(initialLocation)}
                                />
                                <Breadcrumbs pageLoadId={pageLoadId} />
                              </PageContainer>

                              <TopLeaderboardAdSlot isMobileForAds={isMobileForAds} />

                              <PageContainer bottom="6rem" top={spacing.default}>
                                <SrpHeader
                                  h1={pageH1}
                                  currentPage={currentPage}
                                  loading={isInitialLoad}
                                />

                                <SrpGrid>
                                  <FiltersSidebar isMobileForAds={isMobileForAds} />

                                  <Flex flexDirection="column" gap={spacing.default}>
                                    <SrpResultsSort />

                                    <AutoChangeRadiusLocationSystemMessage />

                                    <AdSense id="AFSTop" />

                                    {renderSearchListings(seeAllTopAds)}

                                    <BaseLeaderboardAdSlot />
                                  </Flex>
                                </SrpGrid>

                                <ContentfulContent textJson={textJson} />
                                <SeoLinks />

                                <AppPenRealEstateModal categoryId={initialCategory.id} />
                                <SearchFloatingButtons isMobileForAds={isMobileForAds} />
                              </PageContainer>
                            </>
                          )}
                        </ErrorBoundary>
                      </BaseLayout>
                    </>
                  </SearchAdsWrapper>
                )}
              </SearchContext.Consumer>
            </SearchProvider>
          </SrpPageProvider>
        )}
      </SeoMetadata>
    </>
  )
}

const isRedirectUrl = (redirect: GetSrpPropsWithCache): redirect is PageRedirect =>
  !!(redirect as PageRedirect).redirect

export const getServerSideProps: GetServerSideProps = async (context) => {
  const gSSPstart = Date.now()
  const { query, locale = ROUTE_LOCALE.en, req, res } = context

  const transaction = logger.startTransaction({ name: 'SRP getServerSideProps' })
  const translationsSpan = transaction.startChild({ op: 'Locale & Translations' })

  const cookieLocale = req.cookies[CookieRegistry.SITE_LOCALE] || COOKIE_LOCALE.en_CA

  /** TODO: Verify why we are using NEXT_PUBLIC_USE_LOCALE_FROM_COOKIE to decide get from cookies */
  const { languageKey } = getLocale(
    process.env.NEXT_PUBLIC_USE_LOCALE_FROM_COOKIE === 'true' ? cookieLocale : locale
  )

  const serverI18nStart = Date.now()
  const translations = await serverSideTranslations(languageKey, ['srp', 'feedback'])

  if (process.env.ENABLE_DEBUG === 'true') {
    const serverTimingHeader = res.getHeader('Server-Timing')

    res.setHeader(
      'Server-Timing',
      `${serverTimingHeader || ''}server-i18n;dur=${Date.now() - serverI18nStart},`
    )
  }

  translationsSpan.finish()

  const url = getSrpComposedUrl(query)
  const userAgent = req.headers['user-agent']

  /**
   * When the URL doesn't contain the location information
   * then use the user preferences to execute the SRP/BRP search
   * AND url gets updated to match the location search
   */
  const locationlessUrlSpan = transaction.startChild({ op: 'LocationlessUrl' })
  const locationlessRedirect = await redirectLocationlessUrl({ url, cookies: req.cookies })
  locationlessUrlSpan.finish()
  if (locationlessRedirect) {
    transaction.finish()
    return { redirect: locationlessRedirect.redirect }
  }

  const forwardedHeaders = {
    'user-agent': req.headers['user-agent'],
    'x-forwarded-for':
      req.headers['x-forwarded-for'] || extractIpv4(req.socket.remoteAddress || ''),
  }

  const srpPropsOrRedirect = await getSrpLoadData({
    requestHeaders: {
      headers: forwardedHeaders,
      cookies: req.cookies,
    },
    url,
    userAgent,
    transaction,
  })

  if (isRedirectUrl(srpPropsOrRedirect)) {
    transaction.finish()
    return { redirect: srpPropsOrRedirect.redirect }
  }

  const sessionStart = Date.now()
  const session = await getServerSession(req, res, getServerAuthOptions(req, res))

  if (process.env.ENABLE_DEBUG === 'true') {
    const serverTimingHeader = res.getHeader('Server-Timing')

    res.setHeader(
      'Server-Timing',
      `${serverTimingHeader || ''}server-session;dur=${Date.now() - sessionStart},`
    )
  }

  transaction.finish()

  if (process.env.ENABLE_DEBUG === 'true') {
    const serverTimingHeader = res.getHeader('Server-Timing')

    res.setHeader(
      'Server-Timing',
      `${serverTimingHeader || ''}server-props;dur=${Date.now() - gSSPstart},`
    )
  }

  return { props: { ...srpPropsOrRedirect, userAgent, session, ...translations } }
}

export default SearchPage
